/// <reference path="../types/firebase-legacy.d.ts" />
import {RWSService} from '@rws-framework/client';
import { HTTPClient, HTTPError } from "../services/httpClient";
import type { EdrnaConfig } from 'src/types/window';
import type { AnnouncementMongo } from 'src/types/announcement';
import { dispatchJnctEvent } from "../helpers/jnctEvents";

const config = (window as unknown as EdrnaConfig).edrnaConfig;
const firebaseApiURL = config?.firebase?.apiURL || config?.firebase?.url;
const API_PREFIX = '/server/';
type State = {
  ref: FirebaseRef | undefined;
  uid: string | undefined;
  connection: FirebaseRef | undefined;
  connection_meta: any;
  history: any;
}

class FirebaseService extends RWSService {
  static _IN_CLIENT: boolean = true;
  private state: State = {
    ref: undefined,
    uid: undefined,
    connection: undefined,
    connection_meta: undefined,
    history: undefined,
  };
  private connectingInProgress: boolean = false;
  private connections: any[] = [];
  private announcementQuery: any = null

  public async addOld(
    key: string,
    announcement: AnnouncementMongo
  ): Promise<void> {
    const path = `/${key.replaceAll('.', '_')}/updates/${announcement._id}`;
    const ann = {
      ...announcement,
      timestamp: new Date().toISOString()
    }
    await new Promise<void>((resolve, reject) => {
      this.state.ref.child(path).update(ann, (error: Error | null) => {
        if (error) reject(error);
        else resolve();
      });
    });
  }
  
  async connect(reseller?: string): Promise<{ status: string; uid?: string }> {
    reseller = reseller.replaceAll('.', '_')
    if (this.connectingInProgress) {
      // queue up requests while connecting
      let resolver: (value: { status: string; uid?: string }) => void;
      const promise = new Promise<{ status: string; uid?: string }>((resolve) => {
        resolver = resolve;
      });
      this.connections.push({ resolve: resolver });
      return promise;
    }
  
    this.connectingInProgress = true;
    this.state.connection = new Firebase(firebaseApiURL);

    try {
      const url = API_PREFIX + "firebase_token/";
      const response = await fetch(url, {
        method: reseller ? "POST" : "GET",
        headers: { "Content-Type": "application/json" },
        body: reseller ? JSON.stringify({ course_id: reseller }) : undefined,
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
  
      const data = await response.json();
  
      const existingAuth = this.state.connection.getAuth()
      if (existingAuth) {
        this.state.uid = existingAuth.uid
        this.state.ref = this.state.connection.child("announcement")
        this.connectingInProgress = false
        return { status: "Already authenticated", uid: existingAuth.uid }
      }

    const authResult = await new Promise<{ uid: string }>((resolve, reject) => {
        this.state.connection.authWithCustomToken(
          data.token,
          (error: any, authData: any) => {
            if (error) {
              this.state = this._clear_state();
              reject({ status: error.code });
            } else {
              resolve(authData);
            }
          }
        )
      })

      this.state.uid = authResult.uid;
      this.state.ref = this.state.connection.child("announcement");

      const result = { status: "Connected", uid: this.state.uid };

      // release queued calls
      this.connectingInProgress = false;
      this.connections.forEach((c) => c.resolve({ status: "Already connected", uid: this.state.uid }));
      this.connections = [];

      return result;
    } catch (reason) {
      this.state = this._clear_state();
      this.connectingInProgress = false;
      throw { status: JSON.stringify(reason) };
    }
  }

  subscribeToAnnouncementUpdates() {
    const reseller = window.labelBundle?.reseller?.replaceAll('.', '_')
    const updatesRef = this.state.ref.child(`${reseller}/updates`)
    const now = new Date().toISOString()

    if (this.announcementQuery) {
      return
    }

    try {
      this.announcementQuery = updatesRef.orderByChild('timestamp').startAt(now)
    } catch (error) {
      console.error(error)
    }

    (['child_added', 'child_changed'] as const).forEach((event) =>
      this.announcementQuery.on(event, (snapshot) => {
        const announcement = snapshot.val()
        if (!announcement) return

        dispatchJnctEvent({
          eventName: 'jnct:firebaseservice:event',
          payload: { type: 'all', payload: announcement },
          target: document.documentElement,
        });
      }, console.error)
    )
  }

  listen() {
    const firebaseRef = new Firebase(firebaseApiURL);
    firebaseRef.child('.info/connected').on('value', (connectedSnap) => {
      const connected = connectedSnap.val() === true;
      if (connected) {
        firebaseRef.child('release').on('child_changed', (snapshot) => {
          if (snapshot.key() == "version" && snapshot.val() != config.APIversion) {
            const released_new_version = snapshot.val();
            dispatchJnctEvent({
              eventName: 'jnct:firebaseservice:event',
              payload: { type: 'listen', payload: released_new_version },
              target: document.documentElement,
            });
          }
        });
      } else {
        firebaseRef.child('release').off('child_changed');
      }
    });
  };

  unsubscribeFromAnnouncementUpdates() {
    if (!this.announcementQuery) {
      return
    }

    this.announcementQuery.off('child_added')
    this.announcementQuery.off('child_changed')
    this.announcementQuery = null
  }

  _clear_state() {
    return {
      ref: undefined,
      uid: undefined,
      connection: undefined,
      connection_meta: undefined,
      history: undefined
    }
  }
};

export default FirebaseService.getSingleton()

export {FirebaseService as FirebaseServiceInstance}
